/**
 * The shell's glyph set — inline SVG components, never an icon font and never
 * a remote icon CDN (PRD-TDD §6.3).
 *
 * Every glyph is drawn on the design system's own grid at its own weight:
 * `24×24`, `stroke-width: 2`, round caps and joins, `currentColor`. The set is
 * a STYLE CONTRACT rather than a closed inventory — a new glyph is drawn to
 * these rules rather than imported from a pack.
 *
 * Two rules bind specific glyphs here:
 *
 *   • **The radial form belongs to the theme toggle alone.** A gear, an
 *     asterisk, a sun and a brightness mark all converge on «a centre with
 *     strokes radiating from it» at 18px, and what separates a gear from a sun
 *     is a ring the teeth attach to — a 1px ring inside a 9px circle does not
 *     survive. So the settings entry is a WRENCH: a diagonal handle with an
 *     open jaw, read from its axis rather than from its stroke count.
 *   • **Nothing here is mirrored except the disclosure caret**, which points
 *     along the reading direction. Direction in reading mirrors; an object
 *     does not.
 *
 * Every glyph is `aria-hidden`: each one sits beside its own text label, so
 * announcing it would read the entry twice.
 */

import type { ReactNode } from 'react';

export interface IconProps {
  className?: string;
  size?: number;
}

interface GlyphProps extends IconProps {
  children: ReactNode;
  /** Filled glyphs (the play triangle, the star) opt out of the stroke set. */
  filled?: boolean;
}

function Glyph({ children, className, size = 18, filled = false }: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? undefined : 'currentColor'}
      strokeWidth={filled ? undefined : 2}
      strokeLinecap={filled ? undefined : 'round'}
      strokeLinejoin={filled ? undefined : 'round'}
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

export function IconHome(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 11l8-7 8 7v8a1 1 0 01-1 1h-4v-6H9v6H5a1 1 0 01-1-1z" />
    </Glyph>
  );
}

export function IconCategories(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Glyph>
  );
}

export function IconSearch(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="M16.5 16.5L21 21" />
    </Glyph>
  );
}

export function IconEditorial(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </Glyph>
  );
}

/** The disclosure caret — it rotates on open and is NOT mirrored by direction. */
export function IconCaret(props: IconProps) {
  return (
    <Glyph size={14} {...props}>
      <path d="M6 9l6 6 6-6" />
    </Glyph>
  );
}

/** The radial form. Reserved for the display toggle and used nowhere else. */
/**
 * The SUN, shown while the DARK theme is on — the toggle names what a press
 * switches TO, and the glyph has to agree with the label beside it. A single
 * glyph rendered in both states is a control that never looks pressed, which
 * is what this bar shipped with.
 */
export function IconThemeLight(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
    </Glyph>
  );
}

/** The MOON, shown while the LIGHT theme is on. Pairs with `IconThemeLight`. */
export function IconThemeDark(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />
    </Glyph>
  );
}

export function IconBell(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M18 8.5a6 6 0 10-12 0c0 6-2.2 7.5-2.2 7.5h16.4S18 14.5 18 8.5z" />
      <path d="M13.7 19.5a2 2 0 01-3.4 0" />
    </Glyph>
  );
}

export function IconProfile(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20a7.5 7.5 0 0115 0" />
    </Glyph>
  );
}

export function IconBookmark(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M5 4h14v16l-7-4-7 4z" />
    </Glyph>
  );
}

export function IconSubscription(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M3 10h18" />
    </Glyph>
  );
}

export function IconSupport(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 5h16v11H8l-4 3z" />
    </Glyph>
  );
}

/** Settings. A wrench, deliberately — see the radial-glyph rule in the header. */
export function IconSettings(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
    </Glyph>
  );
}

/**
 * A closed padlock — the security section of settings.
 *
 * Not mirrored: a lock is an object, not a direction in reading, which is the
 * same column the star and the bookmark already sit in.
 */
export function IconLock(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
      <path d="M8 10.5V8a4 4 0 018 0v2.5" />
    </Glyph>
  );
}

/**
 * The pencil — «edit this», wherever an owner acts on their own record.
 *
 * It lives in the shell set rather than in one feature's own because THREE
 * surfaces now draw it: the collection page's edit action, and the open-and-edit
 * action on the collection cards of both `/lists` and `/profile`. That is the
 * two-importer rule the repo applies to every primitive — a glyph is promoted
 * when a second feature imports it, never speculatively.
 *
 * Not mirrored: a tool is an object rather than a direction in reading, the
 * same column the lock, the star and the bookmark already sit in.
 */
export function IconPencil(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 20h4l10-10a2.1 2.1 0 10-3-3L5 17z" />
      <path d="M13.5 6.5l3 3" />
    </Glyph>
  );
}

/**
 * The camera on the avatar — `[x]` §19.5's «upload via the camera control on
 * the profile image». An object, so it is not mirrored either.
 */
export function IconCamera(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M3 8.5h3.5L8 6h8l1.5 2.5H21v11H3z" />
      <circle cx="12" cy="13.5" r="3.5" />
    </Glyph>
  );
}

/**
 * The field-error mark. It sits BESIDE the error sentence rather than instead
 * of it — colour and an icon are two signals and the text is the third, and
 * none of the three is allowed to carry the meaning alone (WCAG 1.4.1).
 */
export function IconFieldError(props: IconProps) {
  return (
    <Glyph size={14} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5M12 16.2v.2" />
    </Glyph>
  );
}

export function IconSignOut(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M14 4h4a1 1 0 011 1v14a1 1 0 01-1 1h-4" />
      <path d="M9 8l-4 4 4 4M5 12h9" />
    </Glyph>
  );
}

export function IconCheck(props: IconProps) {
  return (
    <Glyph size={14} {...props}>
      <path d="M5 12.5l4.5 4.5L19 7.5" strokeWidth={2.5} />
    </Glyph>
  );
}

/** A direction in TIME, not in reading — never mirrored. */
export function IconPlay(props: IconProps) {
  return (
    <Glyph filled {...props}>
      <path d="M8 5v14l11-7z" />
    </Glyph>
  );
}

export function IconPause(props: IconProps) {
  return (
    <Glyph filled {...props}>
      <path d="M8 5h3v14H8zM13 5h3v14h-3z" />
    </Glyph>
  );
}

export function IconStar(props: IconProps) {
  return (
    <Glyph size={11} filled {...props}>
      <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z" />
    </Glyph>
  );
}

/**
 * The rail's own arrows. `direction` is a spatial move, so the glyph is
 * authored for `rtl` — «next» advances toward the reading end, which is the
 * left — and the CALLER never flips it by hand.
 */
export function IconRailNext(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M15 6l-6 6 6 6" />
    </Glyph>
  );
}

export function IconRailPrev(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M9 6l6 6-6 6" />
    </Glyph>
  );
}

/**
 * A broken chain link — a route that no longer reaches anything.
 *
 * It is an ABSENCE rather than a fault, which is why the frame around it keeps
 * secondary ink while the server-fault glyph below takes `--danger`. Not
 * mirrored: a link is an object, not a direction in reading.
 */
export function IconBrokenLink(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M10 7.5H7.5a4.5 4.5 0 0 0 0 9H10" />
      <path d="M14 7.5h2.5a4.5 4.5 0 0 1 0 9H14" />
      <path d="M9.6 12h1.2M13.2 12h1.2" />
    </Glyph>
  );
}

/**
 * A circle carrying an exclamation — a FAULT, not a warning. The design system
 * reserves red for failure and amber for reversible guidance, so this is the
 * one glyph in the set drawn in `--danger` by its frame.
 *
 * Colour is never the only signal: the heading and the copy beside it say what
 * happened in words (WCAG 1.4.1).
 */
export function IconServerFault(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5" />
      <path d="M12 16.4v.1" />
    </Glyph>
  );
}

/**
 * The retry arc. **Not mirrored** — it is a direction in TIME (undo the last
 * attempt), which the design system's mirroring table keeps on the same side
 * as the player's skip pair rather than the navigation arrows.
 */
export function IconRetry(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20 4v4h-4" />
    </Glyph>
  );
}

/**
 * «الختم» — the mark, drawn INLINE from the design system's own definition: an
 * eye of one almond lid, an iris and a play triangle, reversed in bone on a red
 * plate. Four elements and no more; nothing else survives 16px. An eye reads
 * immediately as «شاهدني / watch me», the triangle names the medium, and the
 * plate makes the mark independent of whatever ground it lands on.
 *
 * The geometry is `design-system/assets/shahedni-eye-seal-color.svg`, glyph for
 * glyph, on its own 128×128 grid — not a re-interpretation at another viewBox,
 * because a second drawing of the same mark is a second mark. That file is now
 * the design system's only carrier of the geometry: the open cut
 * (`shahedni-eye-mark-color.svg`) was deleted, and it is gone as a CUT, not
 * merely as a file. `shahedni-eye-lockup-color.svg` is on disk and is the
 * marketing cut; `BrandLockup` below hardcodes it. Nothing renders the mark
 * without its plate except the one-ink reduction, which this app never draws.
 *
 * ONE cut, used everywhere. §8 no longer carves out the product's own chrome:
 * the top bar, the sidebar and the auth shell draw the same seal as the favicon
 * in the tab strip above them. There is no known-canvas exception because there
 * is no second cut to except into.
 *
 * The two values are LITERAL hex, deliberately, and are NOT theme tokens:
 *
 *   plate  #E50914   the identity red of §2.1, constant
 *   mark   #FBF8F1   bone, 4.52:1 on the plate, over §14's 3:1 graphic floor
 *
 * Writing the plate as `var(--accent)` would be a defect, not a refinement:
 * `--accent` resolves to maroon #A3070E on the light canvas, so the top bar's
 * logo would go maroon while `public/favicon.svg` in the same tab stayed red.
 * One logo colour, everywhere — which is also why there is no theme branch
 * here at all.
 *
 * The editorial indigo is not in the mark: #9095F2 on the #E50914 plate
 * measures 1.77:1, under the 3:1 floor §14 sets for a graphic, and the plate is
 * fixed so it cannot be rescued by lightening. Indigo remains the editorial UI
 * layer's colour, not a brand ink.
 *
 * Beside the wordmark this puts two reds in the lockup — the plate and the
 * word's six diacritic dots. That is intended. The dots are the wordmark's own
 * ink and are not restated to match the plate.
 *
 * The triangle points along the media timeline and is NEVER mirrored under
 * `dir="rtl"`. Enlarging the iris or flattening the lid closes the clearance
 * between them and the strokes merge.
 *
 * Always an inline `<svg>`. `mask-image: url(./logo.svg)` is prohibited — a
 * file-path CSS mask fails silently under `file://` and the logo vanishes with
 * no console error, and a linked file cannot be styled by its host either.
 */
export function BrandMark({ className }: IconProps) {
  return (
    <svg viewBox="0 0 128 128" className={className} aria-hidden="true" focusable="false">
      <rect width="128" height="128" rx="30" fill="#E50914" />
      <g fill="none" stroke="#FBF8F1" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 64 Q64 16 106 64 Q64 112 22 64 Z" />
        <circle cx="64" cy="64" r="16" />
        <path d="M58 56 L77 64 L58 72 Z" fill="#FBF8F1" />
      </g>
    </svg>
  );
}

/**
 * The wordmark «شاهدني» — `design-system/assets/shahedni-eye-wordmark-color.svg`,
 * glyph for glyph, on its own 290.4×100 grid. That colour file is the design
 * system's only carrier of the wordmark geometry since the monoline
 * `shahedni-eye-wordmark.svg` was removed; the outlines are unchanged.
 *
 * OUTLINED PATHS, not live text. Noto Kufi Arabic Light shaped right-to-left
 * by HarfBuzz and converted to paths, so the lockup never depends on a font
 * having loaded and no `letter-spacing` can break an Arabic join. Never re-set
 * this as text, never apply tracking to it, never mirror it.
 *
 * Two inks, both theme tokens, matching the colour cut: the letters take
 * `--ink` and the six diacritic dots of «شاهدني» take `--accent`. The dots
 * carry the whole of the word's colour because they are the only marks with no
 * stem weight — tinting them adds the second ink without shifting the letter
 * colour or the stem-to-counter balance.
 *
 * Decorative here: the accessible name is carried by the `sr-only` text beside
 * it, which stays in the accessibility tree at every width.
 */
export function BrandWordmark({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 290.4 100"
      className={className ?? 'h-[1.2rem] w-[3.485rem]'}
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="var(--ink)"
        d="M17.28 84.97Q8.72 84.97 4.36 79.86Q0 74.75 0 64.95Q0 60.55 0.79 56.98Q1.58 53.41 2.82 50H7.97Q6.81 53.32 6.23 56.77Q5.65 60.22 5.65 64.53Q5.65 72.26 8.64 76.12Q11.63 79.98 18.11 79.98H32.89Q37.62 79.98 40.91 78.7Q44.19 77.41 45.89 75.42Q47.59 73.42 47.59 71.26Q47.59 69.1 45.76 67.9Q43.94 66.69 40.28 65.61Q36.71 64.7 33.72 63.54Q30.73 62.38 28.95 60.42Q27.16 58.47 27.16 55.07Q27.16 52.57 27.87 50.37Q28.57 48.17 29.9 46.1Q32.64 41.69 36.96 39Q41.28 36.3 45.85 36.3Q49.09 36.3 51.5 37.62Q53.74 38.87 55.48 41.03Q57.23 43.19 59.55 47.18Q61.71 51 63.54 53.7Q65.37 56.4 67.44 57.89Q68.94 59.14 70.39 59.59Q71.84 60.05 73.5 60.05H73.92Q74.34 60.05 74.34 60.38V64.62Q74.34 65.03 73.92 65.03H73.5Q70.35 65.03 67.44 63.75Q64.53 62.46 61.54 59.47Q60.3 58.31 59.09 56.44Q57.89 54.57 56.31 52.08Q54.57 49.25 53.16 47.18Q51.74 45.1 50.25 43.94Q47.84 41.94 45.18 41.94Q42.94 41.94 40.78 43.11Q38.62 44.27 36.79 46.14Q34.97 48.01 33.89 50.17Q32.81 52.33 32.81 54.32Q32.81 56.73 35.05 57.85Q37.29 58.97 39.62 59.72Q42.36 60.47 44.44 61.17Q46.51 61.88 47.92 62.46Q50.58 63.7 51.99 65.41Q53.41 67.11 53.41 69.44Q53.41 76.91 48.26 80.94Q43.11 84.97 32.89 84.97ZM19.1 100Q17.44 100 16.36 99.04Q15.28 98.09 15.28 95.93Q15.28 93.69 16.36 92.73Q17.44 91.78 19.1 91.78Q20.76 91.78 21.84 92.82Q22.92 93.85 22.92 95.93Q22.92 97.92 21.89 98.96Q20.85 100 19.1 100ZM31.73 100Q27.99 100 27.99 95.93Q27.99 91.78 31.73 91.78Q33.55 91.78 34.59 92.9Q35.63 94.02 35.63 95.93Q35.63 97.84 34.59 98.92Q33.55 100 31.73 100Z M87.13 16.36Q83.31 16.36 83.31 12.29Q83.31 8.14 87.13 8.14Q88.87 8.14 89.91 9.22Q90.95 10.3 90.95 12.29Q90.95 14.2 89.95 15.28Q88.95 16.36 87.13 16.36ZM73.09 65.03Q72.67 65.03 72.67 64.62V60.38Q72.67 60.05 73.09 60.05H84.39V25H89.95V65.03Z M100.75 60.05H128.32Q127.82 56.4 127.03 52.99Q126.25 49.58 125.08 46.43Q122.67 39.87 118.77 34.59Q114.87 29.32 109.14 25.33L111.21 20.51Q120.43 25.75 126.2 35.96Q131.98 46.18 133.64 60.05H142.28Q142.69 60.05 142.69 60.38V64.62Q142.69 65.03 142.28 65.03H100.75Z M155.23 60.05Q152.24 58.31 150.5 54.94Q148.75 51.58 148.75 47.34Q148.75 42.52 150.79 38.91Q152.82 35.3 156.44 33.26Q160.05 31.23 164.62 31.23Q172.09 31.23 176 35.67Q179.9 40.12 179.9 49V60.05H190.53V43.44Q190.53 31.64 185.34 26.16Q180.15 20.68 169.1 20.68Q165.86 20.68 162.29 21.18Q158.72 21.68 155.32 22.76L153.74 17.86Q157.06 16.78 160.96 16.2Q164.87 15.61 169.1 15.61Q182.31 15.61 189.08 22.67Q195.85 29.73 195.85 42.86V65.03H141.45Q141.03 65.03 141.03 64.62V60.38Q141.03 60.05 141.45 60.05ZM166.2 60.05H174.58V50.17Q174.58 43.11 172.01 39.95Q169.44 36.79 164.2 36.79Q159.55 36.79 156.98 39.62Q154.4 42.44 154.4 47.59Q154.4 53.16 157.68 56.6Q160.96 60.05 166.2 60.05Z M228.41 60.05Q228.82 60.05 228.82 60.38V64.62Q228.82 65.03 228.41 65.03H211.54V1.91H217.11V60.05Z M266.11 7.64Q264.53 7.64 263.54 6.69Q262.54 5.73 262.54 3.82Q262.54 1.83 263.54 0.91Q264.53 0 266.11 0Q267.69 0 268.73 0.96Q269.77 1.91 269.77 3.82Q269.77 5.81 268.69 6.73Q267.61 7.64 266.11 7.64ZM259.97 17.44Q258.31 17.44 257.18 16.45Q256.06 15.45 256.06 13.37Q256.06 11.21 257.18 10.22Q258.31 9.22 259.97 9.22Q261.79 9.22 262.79 10.34Q263.79 11.46 263.79 13.37Q263.79 15.2 262.79 16.32Q261.79 17.44 259.97 17.44ZM272.43 17.44Q268.6 17.44 268.6 13.37Q268.6 9.22 272.43 9.22Q274.25 9.22 275.25 10.34Q276.25 11.46 276.25 13.37Q276.25 15.2 275.25 16.32Q274.25 17.44 272.43 17.44ZM279.98 65.03Q276.25 65.03 272.92 63.83Q269.6 62.62 266.69 59.3Q265.03 61.88 262.08 63.46Q259.14 65.03 254.98 65.03H227.57Q227.16 65.03 227.16 64.62V60.38Q227.16 60.05 227.57 60.05H239.95V31.48H245.35V60.05H254.73Q258.8 60.05 261.17 57.48Q263.54 54.9 263.54 50.66V31.48H268.94V51.33Q268.94 51.91 268.85 52.62Q268.77 53.32 268.77 53.99Q270.85 57.39 273.5 58.68Q276.16 59.97 280.07 60.05L284.72 60.13V25.08L290.37 25V65.03Z"
      />
      <path
        fill="var(--accent)"
        d="M19.1 100Q17.44 100 16.36 99.04Q15.28 98.09 15.28 95.93Q15.28 93.69 16.36 92.73Q17.44 91.78 19.1 91.78Q20.76 91.78 21.84 92.82Q22.92 93.85 22.92 95.93Q22.92 97.92 21.89 98.96Q20.85 100 19.1 100Z M31.73 100Q27.99 100 27.99 95.93Q27.99 91.78 31.73 91.78Q33.55 91.78 34.59 92.9Q35.63 94.02 35.63 95.93Q35.63 97.84 34.59 98.92Q33.55 100 31.73 100Z M87.13 16.36Q83.31 16.36 83.31 12.29Q83.31 8.14 87.13 8.14Q88.87 8.14 89.91 9.22Q90.95 10.3 90.95 12.29Q90.95 14.2 89.95 15.28Q88.95 16.36 87.13 16.36Z M266.11 7.64Q264.53 7.64 263.54 6.69Q262.54 5.73 262.54 3.82Q262.54 1.83 263.54 0.91Q264.53 0 266.11 0Q267.69 0 268.73 0.96Q269.77 1.91 269.77 3.82Q269.77 5.81 268.69 6.73Q267.61 7.64 266.11 7.64Z M259.97 17.44Q258.31 17.44 257.18 16.45Q256.06 15.45 256.06 13.37Q256.06 11.21 257.18 10.22Q258.31 9.22 259.97 9.22Q261.79 9.22 262.79 10.34Q263.79 11.46 263.79 13.37Q263.79 15.2 262.79 16.32Q261.79 17.44 259.97 17.44Z M272.43 17.44Q268.6 17.44 268.6 13.37Q268.6 9.22 272.43 9.22Q274.25 9.22 275.25 10.34Q276.25 11.46 276.25 13.37Q276.25 15.2 275.25 16.32Q274.25 17.44 272.43 17.44Z"
      />
    </svg>
  );
}
/**
 * «القفل الأفقي» — the horizontal lockup, hardcoded from
 * `design-system/assets/shahedni-eye-lockup-color.svg`, ONE inline `<svg>` on
 * that file's own `0 0 414.4 100` grid.
 *
 * It is one drawing, not two sized beside each other, and that is the whole
 * point of this shape. The lockup used to be composed from `<BrandMark>` and
 * `<BrandWordmark>` with a class on each, so the two sizes were set by the call
 * site and nothing tied them to the asset: the word shipped at `1.2rem` beside
 * a `2.5rem` seal — **0.48** of the seal's height, where the asset draws both
 * elements exactly `100` units tall. Carrying the asset's own viewBox makes the
 * proportion structural. A call site can change how big the lockup is; it can
 * no longer change what the lockup looks like.
 *
 * Copied from the asset, and not re-derived here:
 *
 *   wordmark   two paths, at `0,0` on the `290.4 × 100` grid
 *   seal       `translate(314.4 0) scale(0.78125)` — `128 → 100`
 *   gap        `24` units, which is 0.24 of the seal's height
 *
 * ONE deviation, deliberate, and it is the ink and never the geometry. The
 * asset self-colours through a `<style>` block and a `prefers-color-scheme`
 * rule, because it is a static cut for marketing. This app's theme is a
 * cookie-backed `data-theme` attribute, so a copied `prefers-color-scheme`
 * block would pin the word to the reader's OS and leave it dark on a dark
 * canvas whenever the two disagree. The word therefore paints from the two
 * theme tokens — `var(--ink)` and `var(--accent)` — which resolve to the same
 * §14 values the asset hardcodes. The SEAL is unchanged: `#E50914` and
 * `#FBF8F1` are literal in the asset because red is the identity colour of
 * §2.1, and they are literal here for the same reason.
 *
 * Always inline. `mask-image: url(./lockup.svg)` is prohibited — a file-path
 * CSS mask fails silently under `file://` and takes the logo with it, and a
 * linked file cannot be styled by its host.
 *
 * §8 sets a `112px` floor on the lockup. At the default `2.5rem` height the
 * lockup is `10.36rem` (≈166px) wide, so a bar with no room passes
 * `markClassName` to draw the seal alone below its breakpoint. The name stays
 * in the accessibility tree at every width.
 */
export function BrandLockup({
  className,
  markClassName,
  lockupClassName,
  label,
}: {
  className?: string;
  markClassName?: string;
  lockupClassName?: string;
  label: string;
}) {
  return (
    <span className={className ?? 'flex shrink-0 items-center text-ink'}>
      {/*
        The seal-only reduction §8 allows below the `112px` floor. Rendered
        only where a call site asks for it, and hidden by that same class at
        the width where the full lockup takes over.
      */}
      {markClassName ? <BrandMark className={markClassName} /> : null}
      <svg
        viewBox="0 0 414.4 100"
        className={lockupClassName ?? 'h-[2.5rem] w-[10.36rem] shrink-0'}
        aria-hidden="true"
        focusable="false"
      >
        <g id="wordmark">
          <path
            fill="var(--ink)"
            d="M17.28 84.97Q8.72 84.97 4.36 79.86Q0 74.75 0 64.95Q0 60.55 0.79 56.98Q1.58 53.41 2.82 50H7.97Q6.81 53.32 6.23 56.77Q5.65 60.22 5.65 64.53Q5.65 72.26 8.64 76.12Q11.63 79.98 18.11 79.98H32.89Q37.62 79.98 40.91 78.7Q44.19 77.41 45.89 75.42Q47.59 73.42 47.59 71.26Q47.59 69.1 45.76 67.9Q43.94 66.69 40.28 65.61Q36.71 64.7 33.72 63.54Q30.73 62.38 28.95 60.42Q27.16 58.47 27.16 55.07Q27.16 52.57 27.87 50.37Q28.57 48.17 29.9 46.1Q32.64 41.69 36.96 39Q41.28 36.3 45.85 36.3Q49.09 36.3 51.5 37.62Q53.74 38.87 55.48 41.03Q57.23 43.19 59.55 47.18Q61.71 51 63.54 53.7Q65.37 56.4 67.44 57.89Q68.94 59.14 70.39 59.59Q71.84 60.05 73.5 60.05H73.92Q74.34 60.05 74.34 60.38V64.62Q74.34 65.03 73.92 65.03H73.5Q70.35 65.03 67.44 63.75Q64.53 62.46 61.54 59.47Q60.3 58.31 59.09 56.44Q57.89 54.57 56.31 52.08Q54.57 49.25 53.16 47.18Q51.74 45.1 50.25 43.94Q47.84 41.94 45.18 41.94Q42.94 41.94 40.78 43.11Q38.62 44.27 36.79 46.14Q34.97 48.01 33.89 50.17Q32.81 52.33 32.81 54.32Q32.81 56.73 35.05 57.85Q37.29 58.97 39.62 59.72Q42.36 60.47 44.44 61.17Q46.51 61.88 47.92 62.46Q50.58 63.7 51.99 65.41Q53.41 67.11 53.41 69.44Q53.41 76.91 48.26 80.94Q43.11 84.97 32.89 84.97ZM19.1 100Q17.44 100 16.36 99.04Q15.28 98.09 15.28 95.93Q15.28 93.69 16.36 92.73Q17.44 91.78 19.1 91.78Q20.76 91.78 21.84 92.82Q22.92 93.85 22.92 95.93Q22.92 97.92 21.89 98.96Q20.85 100 19.1 100ZM31.73 100Q27.99 100 27.99 95.93Q27.99 91.78 31.73 91.78Q33.55 91.78 34.59 92.9Q35.63 94.02 35.63 95.93Q35.63 97.84 34.59 98.92Q33.55 100 31.73 100Z M87.13 16.36Q83.31 16.36 83.31 12.29Q83.31 8.14 87.13 8.14Q88.87 8.14 89.91 9.22Q90.95 10.3 90.95 12.29Q90.95 14.2 89.95 15.28Q88.95 16.36 87.13 16.36ZM73.09 65.03Q72.67 65.03 72.67 64.62V60.38Q72.67 60.05 73.09 60.05H84.39V25H89.95V65.03Z M100.75 60.05H128.32Q127.82 56.4 127.03 52.99Q126.25 49.58 125.08 46.43Q122.67 39.87 118.77 34.59Q114.87 29.32 109.14 25.33L111.21 20.51Q120.43 25.75 126.2 35.96Q131.98 46.18 133.64 60.05H142.28Q142.69 60.05 142.69 60.38V64.62Q142.69 65.03 142.28 65.03H100.75Z M155.23 60.05Q152.24 58.31 150.5 54.94Q148.75 51.58 148.75 47.34Q148.75 42.52 150.79 38.91Q152.82 35.3 156.44 33.26Q160.05 31.23 164.62 31.23Q172.09 31.23 176 35.67Q179.9 40.12 179.9 49V60.05H190.53V43.44Q190.53 31.64 185.34 26.16Q180.15 20.68 169.1 20.68Q165.86 20.68 162.29 21.18Q158.72 21.68 155.32 22.76L153.74 17.86Q157.06 16.78 160.96 16.2Q164.87 15.61 169.1 15.61Q182.31 15.61 189.08 22.67Q195.85 29.73 195.85 42.86V65.03H141.45Q141.03 65.03 141.03 64.62V60.38Q141.03 60.05 141.45 60.05ZM166.2 60.05H174.58V50.17Q174.58 43.11 172.01 39.95Q169.44 36.79 164.2 36.79Q159.55 36.79 156.98 39.62Q154.4 42.44 154.4 47.59Q154.4 53.16 157.68 56.6Q160.96 60.05 166.2 60.05Z M228.41 60.05Q228.82 60.05 228.82 60.38V64.62Q228.82 65.03 228.41 65.03H211.54V1.91H217.11V60.05Z M266.11 7.64Q264.53 7.64 263.54 6.69Q262.54 5.73 262.54 3.82Q262.54 1.83 263.54 0.91Q264.53 0 266.11 0Q267.69 0 268.73 0.96Q269.77 1.91 269.77 3.82Q269.77 5.81 268.69 6.73Q267.61 7.64 266.11 7.64ZM259.97 17.44Q258.31 17.44 257.18 16.45Q256.06 15.45 256.06 13.37Q256.06 11.21 257.18 10.22Q258.31 9.22 259.97 9.22Q261.79 9.22 262.79 10.34Q263.79 11.46 263.79 13.37Q263.79 15.2 262.79 16.32Q261.79 17.44 259.97 17.44ZM272.43 17.44Q268.6 17.44 268.6 13.37Q268.6 9.22 272.43 9.22Q274.25 9.22 275.25 10.34Q276.25 11.46 276.25 13.37Q276.25 15.2 275.25 16.32Q274.25 17.44 272.43 17.44ZM279.98 65.03Q276.25 65.03 272.92 63.83Q269.6 62.62 266.69 59.3Q265.03 61.88 262.08 63.46Q259.14 65.03 254.98 65.03H227.57Q227.16 65.03 227.16 64.62V60.38Q227.16 60.05 227.57 60.05H239.95V31.48H245.35V60.05H254.73Q258.8 60.05 261.17 57.48Q263.54 54.9 263.54 50.66V31.48H268.94V51.33Q268.94 51.91 268.85 52.62Q268.77 53.32 268.77 53.99Q270.85 57.39 273.5 58.68Q276.16 59.97 280.07 60.05L284.72 60.13V25.08L290.37 25V65.03Z"
          />
          <path
            fill="var(--accent)"
            d="M19.1 100Q17.44 100 16.36 99.04Q15.28 98.09 15.28 95.93Q15.28 93.69 16.36 92.73Q17.44 91.78 19.1 91.78Q20.76 91.78 21.84 92.82Q22.92 93.85 22.92 95.93Q22.92 97.92 21.89 98.96Q20.85 100 19.1 100Z M31.73 100Q27.99 100 27.99 95.93Q27.99 91.78 31.73 91.78Q33.55 91.78 34.59 92.9Q35.63 94.02 35.63 95.93Q35.63 97.84 34.59 98.92Q33.55 100 31.73 100Z M87.13 16.36Q83.31 16.36 83.31 12.29Q83.31 8.14 87.13 8.14Q88.87 8.14 89.91 9.22Q90.95 10.3 90.95 12.29Q90.95 14.2 89.95 15.28Q88.95 16.36 87.13 16.36Z M266.11 7.64Q264.53 7.64 263.54 6.69Q262.54 5.73 262.54 3.82Q262.54 1.83 263.54 0.91Q264.53 0 266.11 0Q267.69 0 268.73 0.96Q269.77 1.91 269.77 3.82Q269.77 5.81 268.69 6.73Q267.61 7.64 266.11 7.64Z M259.97 17.44Q258.31 17.44 257.18 16.45Q256.06 15.45 256.06 13.37Q256.06 11.21 257.18 10.22Q258.31 9.22 259.97 9.22Q261.79 9.22 262.79 10.34Q263.79 11.46 263.79 13.37Q263.79 15.2 262.79 16.32Q261.79 17.44 259.97 17.44Z M272.43 17.44Q268.6 17.44 268.6 13.37Q268.6 9.22 272.43 9.22Q274.25 9.22 275.25 10.34Q276.25 11.46 276.25 13.37Q276.25 15.2 275.25 16.32Q274.25 17.44 272.43 17.44Z"
          />
        </g>
        <g transform="translate(314.4 0) scale(0.78125)">
          <rect width="128" height="128" rx="30" fill="#E50914" />
          <g fill="none" stroke="#FBF8F1" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 64 Q64 16 106 64 Q64 112 22 64 Z" />
            <circle cx="64" cy="64" r="16" />
            <path d="M58 56 L77 64 L58 72 Z" fill="#FBF8F1" />
          </g>
        </g>
      </svg>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}
/**
 * The handset — the credential every account on this platform signs in with.
 *
 * It heads the number-change section of `/settings/security` and stands beside
 * the read-only number on `/settings`. An object rather than a direction, so it
 * is not mirrored: the earpiece stays at the top whichever way the page reads.
 */
export function IconPhone(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
    </Glyph>
  );
}

/**
 * The shield — «this is how the account is entered», heading the accepted-method
 * statement.
 *
 * It is NOT the lock: the lock heads the password section directly beneath it,
 * and two sections of one page cannot share a glyph and still be told apart at
 * a glance. The shield carries no check mark, because the row it heads states a
 * configured set rather than a state that is or is not satisfied.
 */
export function IconShield(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M12 3l7 3v5.5c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6z" />
    </Glyph>
  );
}
